import { UnfinishedSave } from '../types/api';

const DAY_MS = 24 * 60 * 60 * 1000;

// When a save started, in the viewer's own time zone: "Sat 2:31 PM" within the last six
// days, "Oct 24 at 2:31 PM" before that ("Oct 24, 2025 at 2:31 PM" when the year is not
// the current one), so a weekday never stands for a date weeks ago.
// Empty for a time it cannot read.
export function formatSaveTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const recent = now.getTime() - d.getTime() < 6 * DAY_MS;
  let text: string;
  if (recent) {
    text = d.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  } else {
    const date = d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const year = d.getFullYear() !== now.getFullYear() ? `, ${d.getFullYear()}` : '';
    text = `${date}${year} at ${d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  // Newer ICU puts a narrow no-break space before AM/PM. Plain spaces read the same everywhere.
  return text.replace(/[  ]/g, ' ');
}

// The bookshelf's notice for a save that didn't finish (#1145). The wording depends on
// whether the file reached the account, because "open it in Chaptery" is only safe
// advice when it did. Wording approved by the owner on 2026-11-02 (spec section 8). The "choose which version to keep" line returns once desktop 1.5.0 ships.
// A book with no finalized revision (`firstSave`: its first save didn't finish) gets one
// wording whatever `uploaded` says. Null when the save's time cannot be read; the server
// always writes one.
export function unfinishedSaveNotice(save: UnfinishedSave, now: Date = new Date(), firstSave = false): string | null {
  const when = formatSaveTime(save.startedAt, now);
  if (!when) return null;
  if (firstSave) {
    return `Your first save of this book from ${when} didn't finish. Your writing is still on the computer you were using. Keep the book open in Chaptery there, and your next save will finish it.`;
  }
  return save.uploaded
    ? `Your save from ${when} didn't finish. It's safe in your account. Open this book in Chaptery on the computer you were using, and your next save will finish it.`
    : `Your save from ${when} didn't reach your account. The changes are still on the computer you were using. Open the book in Chaptery on that computer to save them.`;
}
